/**
 * Keeps every beat buy button ([data-mrk-license-open][data-mrk-product-id]) in sync with the cart: a beat that is in
 * the cart shows "In Cart" (Framer behaviour), so shoppers coming back to the list can see what they already added.
 * Buttons are rendered in the right state by Liquid; this updates them when the cart changes (Horizon's
 * cartLinesUpdate event), when rows are added later (catalog reveal, Section Rendering) and on back/forward cache restores.
 * Clicking an "In Cart" button opens the cart drawer (handled in assets/mrk-license-dialog.js).
 */
import { StandardEvents } from '@shopify/events';

const SELECTOR = '[data-mrk-license-open][data-mrk-product-id]';

/** @type {Set<string> | null} */
let inCart = null;

/** @param {HTMLElement} button */
function apply(button) {
  if (!inCart) return;
  const active = inCart.has(button.dataset.mrkProductId ?? '');
  if (button.hasAttribute('data-mrk-in-cart') === active) return;
  button.toggleAttribute('data-mrk-in-cart', active);
  button.classList.toggle('mrk-is-in-cart', active);
  const label = button.querySelector('[data-mrk-buy-label]');
  if (label instanceof HTMLElement) {
    const text = active ? label.dataset.mrkInCartText : label.dataset.mrkPriceText;
    if (text) label.textContent = text;
  }
  const aria = active ? button.dataset.mrkAriaInCart : button.dataset.mrkAriaBuy;
  if (aria) button.setAttribute('aria-label', aria);
}

/** @param {ParentNode} root */
function applyAll(root = document) {
  for (const button of root.querySelectorAll(SELECTOR)) {
    if (button instanceof HTMLElement) apply(button);
  }
}

async function refresh() {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    inCart = new Set((cart.items || []).map((item) => String(item.product_id)));
    applyAll();
  } catch {
    /* offline or aborted: keep the server-rendered state */
  }
}

if (!window.mrkCartStateBound) {
  window.mrkCartStateBound = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    (promise ?? Promise.resolve()).catch(() => {}).finally(refresh);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });

  new MutationObserver((records) => {
    if (!inCart) return;
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches(SELECTOR)) apply(node);
        else if (node.querySelector(SELECTOR)) applyAll(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}
